// src/components/adhoc/AdhocScanStage.tsx
import React from 'react';
import styled from 'styled-components';
import { mirror } from '../../style/mirror';
import StartButton from '../StartButton';
import Timer from '../Timer';
import { InfoAlert } from '../alert';
import ScanErrorPanel from '../scan-alerts/ScanErrorPanel';
import ScanWarningToast from '../scan-alerts/ScanWarningToast';
import logoSrc from '../../assets/mywellfie-full-logo.png';
import maskGuideSrc from '../../assets/mask-guide.svg';
import './landing.css';
import './scan.css';

const BlurOverlay = styled.div<{ isDesktop: boolean; maskUrl: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  backdrop-filter: blur(10px) brightness(0.55);
  -webkit-backdrop-filter: blur(10px) brightness(0.55);
  background-color: rgba(24, 25, 30, 0.2);
  mask-image: url(${p => p.maskUrl});
  mask-size: ${p => (p.isDesktop ? 'contain' : 'cover')};
  mask-position: center;
  mask-repeat: no-repeat;
  -webkit-mask-image: url(${p => p.maskUrl});
  -webkit-mask-size: ${p => (p.isDesktop ? 'contain' : 'cover')};
  -webkit-mask-position: center;
  -webkit-mask-repeat: no-repeat;
  mask-composite: exclude;
  -webkit-mask-composite: xor;
  pointer-events: none;
`;

const MaskGuide = styled.img<{ isDesktop: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  object-fit: ${p => (p.isDesktop ? 'contain' : 'cover')};
  object-position: center;
  pointer-events: none;
`;

const Video = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  object-fit: cover;
  ${mirror}
`;

interface AdhocScanStageProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  maskUrl: string;
  isDesktop: boolean;
  showCameraLoading: boolean;
  isPreparingScanner: boolean;
  needsCameraEnable?: boolean;
  onEnableCamera?: () => void;
  measuring: boolean;
  processingTime: number;
  showCameraHint: boolean;
  scanError: any;
  scanWarning: any;
  info: any;
  isLoading: boolean;
  canStartMeasure: boolean;
  onButtonClick: () => void;
  onAlertAction: (type: any) => void;
  setVideoReady: (ready: boolean) => void;
}

const AdhocScanStage: React.FC<AdhocScanStageProps> = ({
  videoRef,
  maskUrl,
  isDesktop,
  showCameraLoading,
  isPreparingScanner,
  needsCameraEnable = false,
  onEnableCamera,
  measuring,
  processingTime,
  showCameraHint,
  scanError,
  scanWarning,
  info,
  isLoading,
  canStartMeasure,
  onButtonClick,
  onAlertAction,
  setVideoReady,
}) => (
  <div className={`adhoc-landing adhoc-scan${measuring ? ' adhoc-scan-measuring' : ''}`}>
    {measuring && (
      <div className="scan-measuring-bar">
        <p className="scan-measuring-label">Scan in progress</p>
        <div className="scan-measuring-timer">
          <Timer started={true} durationSeconds={processingTime} variant="dark" />
        </div>
      </div>
    )}

    <main className="scan-main">
      <div className="scan-content">
        <div className="scan-stage">
          <div className="scan-video-wrap">
            <Video
              ref={videoRef}
              id="video"
              muted
              playsInline
              onCanPlay={() => setVideoReady(true)}
              onPlaying={() => setVideoReady(true)}
            />
            <BlurOverlay maskUrl={maskUrl} isDesktop={isDesktop} />
            <MaskGuide src={maskGuideSrc} alt="" isDesktop={isDesktop} />
            {!measuring && (
              <img src={logoSrc} alt="MyWellfie" className="scan-logo-overlay" />
            )}
            {showCameraHint && (
              <p className="scan-camera-hint">
                Stay still and keep your face within the guide for accurate results.
              </p>
            )}
            {showCameraLoading && (
              <div className="scan-loading-overlay">
                <div className="scan-loading-spinner" />
                <p className="scan-loading-title">
                  {isPreparingScanner ? 'Preparing scanner…' : 'Initialising camera…'}
                </p>
                <p className="scan-loading-sub">
                  Please stay still and ensure good lighting
                </p>
              </div>
            )}
            {needsCameraEnable && (
              <div className="scan-loading-overlay">
                <p className="scan-loading-title">Camera access required</p>
                <p className="scan-loading-sub">
                  Allow camera access to continue with your wellness scan.
                </p>
                <button
                  type="button"
                  className="btn btn-journey scan-enable-camera-btn"
                  onClick={onEnableCamera}
                >
                  Allow camera
                </button>
              </div>
            )}
          </div>

          {measuring && (
            <button
              type="button"
              className="scan-stop-mini"
              onClick={onButtonClick}
              aria-label="Stop scan"
            >
              stop
            </button>
          )}
          {measuring && scanWarning && (
            <ScanWarningToast alert={scanWarning} onAction={t => onAlertAction(t)} />
          )}
          {measuring && <InfoAlert message={info.message} />}

          {(!measuring || scanError) && (
            <div className="scan-control-float">
              {scanError ? (
                <ScanErrorPanel alert={scanError} onAction={t => onAlertAction(t)} />
              ) : (
                <StartButton
                  isLoading={isLoading}
                  isMeasuring={false}
                  disabled={!canStartMeasure}
                  onClick={onButtonClick}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </main>
  </div>
);

export default AdhocScanStage;
